// src/api/courses.ts

import { BASE_URL } from "../utils/config";
import { Course, Hole, HoleReadyStatus, Product } from "../types/api";
import { messageToast } from "../components/common/toastMessage"; // Direct UI call - consider refactoring
import { getAuthToken } from "../services/authService"; // Needed for product options

/**
 * Fetches a list of all available courses.
 * @returns A promise resolving to an array of Course objects.
 * @throws {Error} If the request fails.
 */
export async function fetchCourses(): Promise<Course[]> {
	const url = `${BASE_URL}/course`;
	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			throw new Error(`Failed to fetch courses: ${response.status}`);
		}
		return (await response.json()) as Course[];
	} catch (error: any) {
		console.error("Error fetching courses:", error);
		messageToast({
			message: "An error occurred while retrieving the course data",
			background: "text-bg-danger",
		}); // Fixed to use options object
		throw new Error(error.message || "An error occurred while fetching courses.");
	}
}

/**
 * Fetches details for a specific hole by its slug.
 * @param holeSlug - The slug of the hole.
 * @returns A promise resolving to the Hole object.
 * @throws {Error} If the request fails.
 */
export async function fetchHoleDetails(holeSlug: string): Promise<Hole> {
	const url = `${BASE_URL}/hole/${holeSlug}`;
	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			if (response.status === 404) {
				throw new Error("HoleNotFound"); // Specific error for 404
			}
			throw new Error(`Failed to fetch hole details: ${response.status}`);
		}
		return (await response.json()) as Hole;
	} catch (error: any) {
		console.error(`Error fetching hole details for slug ${holeSlug}:`, error);
		// Avoid toast here, let caller handle error
		throw new Error(
			error.message || "An error occurred while fetching hole details."
		);
	}
}

/**
 * Checks if a specific hole's cameras are ready.
 * @param holeSlug - The slug of the hole.
 * @returns A promise resolving to true if ready, false otherwise.
 */
export async function checkHoleReady(holeSlug: string): Promise<boolean> {
	const url = `${BASE_URL}/hole/${holeSlug}/hole_ready`;
	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
		});

		// Try to parse the response even if status is not ok (e.g., 503 might still have is_ready in body)
		let data: HoleReadyStatus;
		try {
			data = await response.json();
		} catch (parseError) {
			console.error(
				`Error parsing hole readiness response: ${response.status} ${response.statusText}`
			);
			return false;
		}

		// Check if response was ok or if we got valid data
		if (!response.ok && !data.hasOwnProperty('is_ready')) {
			console.error(
				`Error checking hole readiness: ${response.status} ${response.statusText}`
			);
			return false;
		}

		return data.is_ready === true;
	} catch (error) {
		console.error(
			`Error checking hole ready status for slug ${holeSlug}:`,
			error
		);
		return false; // Treat network errors as not ready
	}
}

export async function getCourseBySlug(
	courseSlug: string
): Promise<Course | null> {
	const url = `${BASE_URL}/course/${courseSlug}`;
	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			throw new Error(`Failed to fetch courses: ${response.status}`);
		}
		return (await response.json()) as Course | null;
	} catch (error: any) {
		console.error("Error fetching courses:", error);
		messageToast({
			message: "An error occurred while retrieving the course data",
			background: "text-bg-danger",
		}); // Fixed to use options object
		throw new Error(error.message || "An error occurred while fetching courses.");
	}
}
